import { useState, useCallback, useMemo } from 'react';
import type { DrawerTab } from '../components/TransactionDrawer';
import type { TransformedInvoice } from '../types';

interface GroupedInvoices {
  period: string;
  transactions: TransformedInvoice[];
}

export function useTransactionDrawerState(groupedInvoices: GroupedInvoices[]) {
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeDrawerTab, setActiveDrawerTab] = useState<DrawerTab>('details');

  const activeRow = useMemo(() => {
    if (!activeRowId) return null;
    for (const group of groupedInvoices) {
      const found = group.transactions.find(t => t.id === activeRowId);
      if (found) return found;
    }
    return null;
  }, [activeRowId, groupedInvoices]);

  const handleRowClick = useCallback((invoiceId: string) => {
    setActiveRowId(prev => (prev === invoiceId ? prev : invoiceId));
  }, []);

  const handleRowDoubleClick = useCallback((invoiceId: string) => {
    setActiveRowId(prevActiveRowId => {
      const isSameRow = prevActiveRowId === invoiceId;
      setActiveDrawerTab('details');
      setIsDrawerOpen(prevIsOpen => (isSameRow ? !prevIsOpen : true));
      return invoiceId;
    });
  }, []);

  const handleDrawerTabChange = useCallback((tab: DrawerTab) => {
    setActiveDrawerTab(tab);
    setIsDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const openDrawerToTab = useCallback((invoiceId: string, tab: DrawerTab) => {
    setActiveRowId(invoiceId);
    setActiveDrawerTab(tab);
    setIsDrawerOpen(true);
  }, []);

  return {
    activeRowId,
    activeRow,
    isDrawerOpen,
    activeDrawerTab,
    handleRowClick,
    handleRowDoubleClick,
    handleDrawerTabChange,
    handleDrawerClose,
    openDrawerToTab,
  };
}
